import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { FaGift } from 'react-icons/fa';
import ReadOnlyEditor from '../components/tiptap-templates/simple/read-only-editor';

export default function PointPrizes({ 
  prizes = [], 
  points = { redeemable: 0 }, 
  onRedeemClick,
  isMobile 
}) {
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleRedeemClick = (prize) => {
    if (onRedeemClick) {
      onRedeemClick(prize);
    }
  };

  return (
    <Box mt={3}>
      <Typography variant="body2" color="text.white" mb={3} sx={{ px: 1,
            color: '#fff' }}>
        Redeem your hard-earned points for exciting rewards or cash!
      </Typography>

      <Grid container spacing={isMobile ? 2 : 3}>
        {prizes && prizes.length > 0 ? (
          prizes.map((prize, index) => {
            const isCash = prize.type === 'cash';
            const canRedeem = points?.redeemable >= prize.points_required;
            const isOutOfStock = !prize.is_active || prize.stock === 0;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={prize.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card
                    onMouseEnter={() => setHoveredCard(prize.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    sx={{
                      borderRadius: '16px',
                      height: '100%',
                      position: 'relative',
                      overflow: 'visible',
                      bgcolor: isOutOfStock ? 'rgba(250,250,250,0.8)' : 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(10px)',
                      opacity: isOutOfStock ? 0.6 : 1,
                      transition: 'all 0.3s ease',
                      border: hoveredCard === prize.id ? `2px solid ${prize.color}` : '1px solid rgba(0,0,0,0.06)',
                      boxShadow: hoveredCard === prize.id 
                        ? '0 8px 32px rgba(0,0,0,0.12)' 
                        : '0 2px 8px rgba(0,0,0,0.04)',
                      '&:hover': {
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    {/* Badges */}
                    {isCash && (
                      <Chip
                        label="💰 Cash"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          left: -10,
                          fontWeight: 'bold',
                          zIndex: 1,
                          bgcolor: '#e65100',
                          color: '#fff',
                          '& .MuiChip-label': { fontWeight: 700 },
                        }}
                      />
                    )}
                    {isOutOfStock && (
                      <Chip
                        label="Not Available"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          fontWeight: 'bold',
                          zIndex: 1,
                          bgcolor: '#f44336',
                          color: '#fff',
                          '& .MuiChip-label': { fontWeight: 700 },
                        }}
                      />
                    )}
                    {!isOutOfStock && !isCash && prize.stock < 5 && (
                      <Chip
                        label={`Only ${prize.stock} left!`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          fontWeight: 'bold',
                          zIndex: 1,
                          bgcolor: '#ff9800',
                          color: '#fff',
                          '& .MuiChip-label': { fontWeight: 700 },
                        }}
                      />
                    )}

                    <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                      {/* Icon & Title */}
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Avatar 
                          sx={{ 
                            background: prize.gradient || 'linear-gradient(135deg, #2e7d32, #43a047)',
                            width: isMobile ? 44 : 48, 
                            height: isMobile ? 44 : 48,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          }}
                        >
                          {prize.icon || <FaGift size={24} color="#fff" />}
                        </Avatar>
                        <Box flex={1}>
                          <Typography variant="h6" fontWeight={600} sx={{ fontSize: isMobile ? '0.95rem' : '1.1rem', color: '#1a1a2e' }}>
                            {prize.name}
                          </Typography>
                          <Chip
                            label={prize.category}
                            size="small"
                            sx={{ 
                              bgcolor: isCash ? 'rgba(230,81,0,0.1)' : 'rgba(46,125,50,0.1)',
                              color: isCash ? '#e65100' : '#2e7d32',
                              fontSize: '0.6rem',
                              height: 20,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Description */}
                      {/* <Typography 
                        variant="body2" 
                        sx={{ 
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: 40,
                          fontSize: '0.85rem',
                          color: '#424242',
                        }}
                      >
                        {prize.description}
                      </Typography> */}
                       <Box 
                          sx={{ width: '100%',minWidth: '25vw', mb: 1.5,
                            minHeight: { xs: 54, sm: 58 },
                            // maxWidth: { xs: 58, sm: 62 },
                              overflow: 'hidden',
                              color: 'text.secondary',
                                fontSize: '0.875rem',
                                lineHeight: 1.55,
                                  '& p': { margin: 0, },
                                  '& ul, & ol': { margin: '4px 0',
                                    paddingLeft: '20px', }, 
                                    '& strong': { color: 'text.primary', },
                                    display: '-webkit-box',
                                    //  WebkitLineClamp: 3,
                                    //   WebkitBoxOrient: 'vertical',
                                    //    textOverflow: 'ellipsis',
                                        }}>
  
                          <ReadOnlyEditor content={prize.description}/>    
                        </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Points & Actions */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ color: '#757575', fontWeight: 500 }}>
                            Points Required
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" sx={{ color: isCash ? '#e65100' : '#2e7d32' }}>
                            {prize.points_required} pts
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          onClick={() => handleRedeemClick(prize)}
                          disabled={isOutOfStock || !canRedeem}
                          sx={{
                            bgcolor: isCash ? '#e65100' : '#2e7d32',
                            '&:hover': { 
                              bgcolor: isCash ? '#bf360c' : '#1b5e20' 
                            },
                            '&:disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: isMobile ? '0.7rem' : '0.8rem',
                            px: isMobile ? 1.5 : 2,
                            py: isMobile ? 0.5 : 1,
                            minWidth: isMobile ? 80 : 100,
                          }}
                        >
                          {isOutOfStock ? 'Unavailable' : 
                           !canRedeem ? `Need ${prize.points_required - points.redeemable} more` : 
                           isCash ? 'Withdraw' : 'Redeem'}
                        </Button>
                      </Box>

                      {/* Progress indicator if not enough points */}
                      {!canRedeem && !isOutOfStock && (
                        <Box sx={{ mt: 1.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(points.redeemable / prize.points_required) * 100}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              bgcolor: 'rgba(0,0,0,0.06)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: isCash ? '#e65100' : '#2e7d32',
                              },
                            }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '16px' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No prizes available at the moment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back later for exciting rewards!
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}